import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Chip, Alert, Accordion, AccordionSummary,
  AccordionDetails, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── Swagger-style HTTP method badge ──────────────────────────────────────────
const METHOD_COLORS = {
  GET: '#61AFFE',
  POST: '#49CC90',
  PUT: '#FCA130',
  DELETE: '#F93E3E',
  PATCH: '#50E3C2',
};

const MethodBadge = ({ method }) => {
  const bg = METHOD_COLORS[method] || '#999';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bg,
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.75rem',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        letterSpacing: '0.5px',
        minWidth: 72,
        fontFamily: 'monospace',
      }}
    >
      {method}
    </Box>
  );
};

// ─── Code block (monospace, dark background) ───────────────────────────────────
const CodeBlock = ({ children }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: 'grey.900',
      color: 'grey.100',
      p: 2,
      borderRadius: 1,
      overflow: 'auto',
      fontSize: '0.8rem',
      fontFamily: '"Fira Code", "SF Mono", Menlo, Consolas, monospace',
      m: 0,
      mb: 2,
      border: 1,
      borderColor: 'grey.800',
    }}
  >
    <code>{children}</code>
  </Box>
);

// ─── Param / header / response table ───────────────────────────────────────────
const ParamTable = ({ rows, headers, yesLabel, noLabel }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.50' }}>
          <TableCell sx={{ fontWeight: 700 }}>{headers.field}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{headers.type}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{headers.required}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{headers.description}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i} hover>
            <TableCell>
              <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'primary.main' }}>
                {row.field}
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {row.type}
              </Typography>
            </TableCell>
            <TableCell>
              {row.required
                ? <Chip label={yesLabel} size="small" color="primary" variant="filled" sx={{ height: 22 }} />
                : <Chip label={noLabel} size="small" variant="outlined" sx={{ height: 22 }} />}
            </TableCell>
            <TableCell>
              <Typography variant="body2">{row.description}</Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Endpoint accordion (one per endpoint) ─────────────────────────────────────
const EndpointAccordion = ({
  method, path, scope, scopeLabel, description, children, defaultExpanded = false,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{
      mb: 1.5,
      '&:before': { display: 'none' },
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      '&.Mui-expanded': { my: 1.5 },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        bgcolor: 'grey.50',
        '&.Mui-expanded': { bgcolor: 'grey.50' },
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
        <MethodBadge method={method} />
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.95rem', flex: 1, minWidth: 200 }}>
          {path}
        </Typography>
        {scope && scopeLabel && (
          <Chip
            label={scopeLabel}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontFamily: 'monospace' }}
          />
        )}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
      {children}
    </AccordionDetails>
  </Accordion>
);

// ─── Section title ─────────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      mt: 2,
      mb: 1,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'text.secondary',
      fontSize: '0.75rem',
    }}
  >
    {children}
  </Typography>
);

// ─── Code constants (not translated) ───────────────────────────────────────────
// Ajustar segun el entorno. Para dominios sin puerto, dejar API_PORT como '' (vacio).
const API_PROTOCOL = '<api_protocol>';   // http  o  https
const API_HOST = '<api_host>';           // 172.208.54.76  o  api.example.com
const API_PORT = '<api_port>';           // 3007  o  '' (vacio) si no aplica
const API_BASE_URL = API_PORT
  ? `${API_PROTOCOL}://${API_HOST}:${API_PORT}/api/external/v1`
  : `${API_PROTOCOL}://${API_HOST}/api/external/v1`;

const RESPONSE_HEALTH = `{
  "status": "ok",
  "service": "entropy-external-api",
  "version": "v1"
}`;

const EXAMPLE_CREATE_JOB = `curl -X POST '${API_BASE_URL}/jobs' \\
  -H 'Authorization: Bearer <api_token>' \\
  -H 'Content-Type: application/json' \\
  -H 'Idempotency-Key: 7f4e1b3a-1234-4abc-9def-0123456789ab' \\
  -d '{
    "worktemplate_id": 587,
    "project_id": 47,
    "variables": { "vlan": 100, "site": "BA1" },
    "userInteraction": { "approval": "AUTO" }
  }'`;

const RESPONSE_CREATE_JOB = `{
  "job_id": 98765,
  "status": "WA",
  "request_id": "9f0c8a7b-..."
}`;

const RESPONSE_LIST_JOBS = `{
  "data": [
    {
      "job_id": 456,
      "name": "deploy-prod",
      "status": "RU",
      "worktemplate_id": 12,
      "project_id": 3,
      "type": "AN",
      "type_template": "AN",
      "created": "2026-06-04T10:00:00.000Z",
      "start": "2026-06-04T10:00:05.000Z",
      "finish": null
    }
  ],
  "pagination": {
    "limit": 50,
    "offset": 0,
    "total": 1
  }
}`;

const RESPONSE_GET_JOB = `{
  "job_id": 12345,
  "name": "deploy-prod",
  "status": "FI",
  "worktemplate_id": 12,
  "project_id": 3,
  "type_template": "AN",
  "created": "2026-06-04T10:00:00.000Z",
  "start": "2026-06-04T10:00:05.000Z",
  "finish": "2026-06-04T10:01:30.000Z",
  "ex_result": 0
}`;

const RESPONSE_GET_STATUS = `{
  "job_id": 12345,
  "status": "RU",
  "start": "2026-06-04T10:00:05.000Z",
  "finish": null
}`;

const RESPONSE_CANCEL = `{
  "job_id": 12345,
  "previous_status": "RU",
  "status": "AB"
}`;

// ─── Error chip color helper ───────────────────────────────────────────────────
const errorChipColor = (code) => (['401', '403'].includes(code) ? 'error' : 'warning');

// ─── Main component ────────────────────────────────────────────────────────────
const ApiTokenDocumentation = () => {
  const { t } = useTranslation();

  const tableHeaders = t('apiTokenList.docs.th', { returnObjects: true });
  const commonHeadersData = t('apiTokenList.docs.commonHeadersData', { returnObjects: true });
  const createJobHeadersData = t('apiTokenList.docs.endpoints.createJob.headersData', { returnObjects: true });
  const createJobBodyData = t('apiTokenList.docs.endpoints.createJob.bodyData', { returnObjects: true });
  const createJobErrors = t('apiTokenList.docs.endpoints.createJob.errors', { returnObjects: true });
  const listJobsQueryParamsData = t('apiTokenList.docs.endpoints.listJobs.queryParamsData', { returnObjects: true });
  const listJobsErrors = t('apiTokenList.docs.endpoints.listJobs.errors', { returnObjects: true });
  const getJobPathParamsData = t('apiTokenList.docs.endpoints.getJob.pathParamsData', { returnObjects: true });
  const getJobErrors = t('apiTokenList.docs.endpoints.getJob.errors', { returnObjects: true });
  const getJobStatusPathParamsData = t('apiTokenList.docs.endpoints.getJobStatus.pathParamsData', { returnObjects: true });
  const cancelJobPathParamsData = t('apiTokenList.docs.endpoints.cancelJob.pathParamsData', { returnObjects: true });
  const cancelJobErrors = t('apiTokenList.docs.endpoints.cancelJob.errors', { returnObjects: true });

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* ── Overview ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {t('apiTokenList.docs.title')}
        </Typography>

        <SectionTitle>{t('apiTokenList.docs.urlBase')}</SectionTitle>
        <CodeBlock>{API_BASE_URL}</CodeBlock>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
          {t('apiTokenList.docs.urlBaseNote')}
        </Typography>

        <SectionTitle>{t('apiTokenList.docs.commonHeaders')}</SectionTitle>
        <ParamTable
          rows={Object.values(commonHeadersData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.statusCodes')}</SectionTitle>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Chip label={t('apiTokenList.docs.statusChip.ok')} sx={{ bgcolor: '#49CC90', color: '#fff', fontWeight: 600 }} />
          <Chip label={t('apiTokenList.docs.statusChip.accepted')} sx={{ bgcolor: '#49CC90', color: '#fff', fontWeight: 600 }} />
          <Chip label={t('apiTokenList.docs.statusChip.unauthorized')} color="error" variant="outlined" />
          <Chip label={t('apiTokenList.docs.statusChip.forbidden')} color="error" variant="outlined" />
          <Chip label={t('apiTokenList.docs.statusChip.notFound')} color="warning" variant="outlined" />
          <Chip label={t('apiTokenList.docs.statusChip.conflict')} color="warning" variant="outlined" />
          <Chip label={t('apiTokenList.docs.statusChip.unprocessable')} color="warning" variant="outlined" />
          <Chip label={t('apiTokenList.docs.statusChip.rateLimit')} color="error" variant="outlined" />
        </Box>
      </Paper>

      {/* ── Endpoints ── */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        {t('apiTokenList.docs.endpointsLabel')}
      </Typography>

      {/* GET /health */}
      <EndpointAccordion
        method="GET"
        path="/health"
        description={t('apiTokenList.docs.endpoints.health.description')}
        defaultExpanded
      >
        <SectionTitle>{t('apiTokenList.docs.section.response200')}</SectionTitle>
        <CodeBlock>{RESPONSE_HEALTH}</CodeBlock>
      </EndpointAccordion>

      {/* POST /jobs */}
      <EndpointAccordion
        method="POST"
        path="/jobs"
        scope="jobs.run"
        scopeLabel={t('apiTokenList.docs.scopeLabel', { scope: 'jobs.run' })}
        description={t('apiTokenList.docs.endpoints.createJob.description')}
      >
        <SectionTitle>{t('apiTokenList.docs.section.idempotencyTitle')}</SectionTitle>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('apiTokenList.docs.idempotency.intro')}
        </Alert>
        <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
          <li><strong>{t('apiTokenList.docs.idempotency.rule1Prefix')}</strong> {t('apiTokenList.docs.idempotency.rule1Body')}</li>
          <li><strong>{t('apiTokenList.docs.idempotency.rule2Prefix')}</strong> {t('apiTokenList.docs.idempotency.rule2Body')}</li>
          <li><strong>{t('apiTokenList.docs.idempotency.rule3Prefix')}</strong> {t('apiTokenList.docs.idempotency.rule3Body')}</li>
          <li>{t('apiTokenList.docs.idempotency.rule4')}</li>
        </Box>

        <SectionTitle>{t('apiTokenList.docs.section.headersRequired')}</SectionTitle>
        <ParamTable
          rows={Object.values(createJobHeadersData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.body')}</SectionTitle>
        <ParamTable
          rows={Object.values(createJobBodyData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.example')}</SectionTitle>
        <CodeBlock>{EXAMPLE_CREATE_JOB}</CodeBlock>

        <SectionTitle>{t('apiTokenList.docs.section.response202')}</SectionTitle>
        <CodeBlock>{RESPONSE_CREATE_JOB}</CodeBlock>

        <SectionTitle>{t('apiTokenList.docs.section.errors')}</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Object.entries(createJobErrors).map(([code, msg]) => (
            <Box key={code}>
              <Chip label={code} size="small" color={errorChipColor(code)} sx={{ mr: 1 }} />
              {msg}
            </Box>
          ))}
        </Box>
      </EndpointAccordion>

      {/* GET /jobs */}
      <EndpointAccordion
        method="GET"
        path="/jobs"
        scope="jobs.read"
        scopeLabel={t('apiTokenList.docs.scopeLabel', { scope: 'jobs.read' })}
        description={t('apiTokenList.docs.endpoints.listJobs.description')}
      >
        <SectionTitle>{t('apiTokenList.docs.section.queryParams')}</SectionTitle>
        <ParamTable
          rows={Object.values(listJobsQueryParamsData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.response200')}</SectionTitle>
        <CodeBlock>{RESPONSE_LIST_JOBS}</CodeBlock>
        <Typography variant="caption" color="text.secondary">
          {t('apiTokenList.docs.endpoints.listJobs.note')}
        </Typography>
      </EndpointAccordion>

      {/* GET /jobs/:jobId */}
      <EndpointAccordion
        method="GET"
        path="/jobs/:jobId"
        scope="jobs.read"
        scopeLabel={t('apiTokenList.docs.scopeLabel', { scope: 'jobs.read' })}
        description={t('apiTokenList.docs.endpoints.getJob.description')}
      >
        <SectionTitle>{t('apiTokenList.docs.section.pathParams')}</SectionTitle>
        <ParamTable
          rows={Object.values(getJobPathParamsData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.response200')}</SectionTitle>
        <CodeBlock>{RESPONSE_GET_JOB}</CodeBlock>

        <SectionTitle>{t('apiTokenList.docs.section.errors')}</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Object.entries(getJobErrors).map(([code, msg]) => (
            <Box key={code}>
              <Chip label={code} size="small" color={errorChipColor(code)} sx={{ mr: 1 }} />
              {msg}
            </Box>
          ))}
        </Box>
      </EndpointAccordion>

      {/* GET /jobs/:jobId/status */}
      <EndpointAccordion
        method="GET"
        path="/jobs/:jobId/status"
        scope="jobs.read"
        scopeLabel={t('apiTokenList.docs.scopeLabel', { scope: 'jobs.read' })}
        description={t('apiTokenList.docs.endpoints.getJobStatus.description')}
      >
        <SectionTitle>{t('apiTokenList.docs.section.pathParams')}</SectionTitle>
        <ParamTable
          rows={Object.values(getJobStatusPathParamsData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.response200')}</SectionTitle>
        <CodeBlock>{RESPONSE_GET_STATUS}</CodeBlock>
      </EndpointAccordion>

      {/* POST /jobs/:jobId/cancel */}
      <EndpointAccordion
        method="POST"
        path="/jobs/:jobId/cancel"
        scope="jobs.abort"
        scopeLabel={t('apiTokenList.docs.scopeLabel', { scope: 'jobs.abort' })}
        description={t('apiTokenList.docs.endpoints.cancelJob.description')}
      >
        <SectionTitle>{t('apiTokenList.docs.section.pathParams')}</SectionTitle>
        <ParamTable
          rows={Object.values(cancelJobPathParamsData)}
          headers={tableHeaders}
          yesLabel={tableHeaders.yes}
          noLabel={tableHeaders.no}
        />

        <SectionTitle>{t('apiTokenList.docs.section.response200')}</SectionTitle>
        <CodeBlock>{RESPONSE_CANCEL}</CodeBlock>

        <SectionTitle>{t('apiTokenList.docs.section.errors')}</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Object.entries(cancelJobErrors).map(([code, msg]) => (
            <Box key={code}>
              <Chip label={code} size="small" color={errorChipColor(code)} sx={{ mr: 1 }} />
              {msg}
            </Box>
          ))}
        </Box>
      </EndpointAccordion>
    </Box>
  );
};

export default ApiTokenDocumentation;
